import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../../firebase/firebaseConfig";
import { Button } from "../../../../components/ui/form/button";
import { collection, doc, serverTimestamp, writeBatch } from "firebase/firestore";
import { toast } from "react-hot-toast";
import { logClassActivity } from "../../api/activityLogger";
import { FiSend, FiX } from "react-icons/fi";
import dayjs from "dayjs";

const todayStr = () => dayjs().format("YYYY-MM-DD");
const fmt = (d) => dayjs(d).format("DD/MM/YYYY");
const isOver = (deadline) => dayjs(deadline).endOf("day").isBefore(dayjs());

export default function EditAssignmentModal({ open, onClose, classInfo, students = [], assignment }) {
  const [title, setTitle] = useState(assignment?.title || "");
  const [note, setNote] = useState(assignment?.note || "");
  const [startAt, setStartAt] = useState(assignment?.startAt || todayStr());
  const [deadline, setDeadline] = useState(assignment?.deadline || todayStr());
  const [allowLate, setAllowLate] = useState(!!assignment?.allowLate);
  const [maxAttempts, setMaxAttempts] = useState(assignment?.maxAttempts || 1);
  const [saving, setSaving] = useState(false);
  const [sendNotifForNew, setSendNotifForNew] = useState(true);

  // NEW: published
  const [published, setPublished] = useState(!!assignment?.published);

  const hasSubset = Array.isArray(assignment?.assignedTo) && assignment.assignedTo.length > 0;
  const [assignToAll, setAssignToAll] = useState(!hasSubset);
  const [studentQuery, setStudentQuery] = useState("");
  const [selectedStudentIds, setSelectedStudentIds] = useState(hasSubset ? assignment.assignedTo : []);

  useEffect(() => {
    if (!open || !assignment) return;
    setTitle(assignment.title || "");
    setNote(assignment.note || "");
    setStartAt(assignment.startAt || todayStr());
    setDeadline(assignment.deadline || todayStr());
    setAllowLate(!!assignment.allowLate);
    setMaxAttempts(assignment.maxAttempts || 1);
    const _hasSubset = Array.isArray(assignment.assignedTo) && assignment.assignedTo.length > 0;
    setAssignToAll(!_hasSubset);
    setSelectedStudentIds(_hasSubset ? assignment.assignedTo : []);
    setPublished(!!assignment.published); // NEW
  }, [open, assignment]);

  const filteredStudents = useMemo(() => {
    const q = studentQuery.toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const name = (s.fullName || `${s.firstName || ""} ${s.lastName || ""}`).toLowerCase();
      const email = (s.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [students, studentQuery]);

  const allVisibleChecked = useMemo(() => {
    if (!filteredStudents.length) return false;
    return filteredStudents.every((s) => selectedStudentIds.includes(s.id));
  }, [filteredStudents, selectedStudentIds]);

  const handleToggleAllVisible = () => {
    if (allVisibleChecked) {
      const set = new Set(selectedStudentIds);
      filteredStudents.forEach((s) => set.delete(s.id));
      setSelectedStudentIds([...set]);
    } else {
      const set = new Set(selectedStudentIds);
      filteredStudents.forEach((s) => set.add(s.id));
      setSelectedStudentIds([...set]);
    }
  };

  const handleToggleOne = (id) => {
    setSelectedStudentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSave = async () => {
    if (!title?.trim()) return toast.error("Vui lòng nhập tiêu đề assignment.");
    if (!startAt || !deadline) return toast.error("Vui lòng chọn ngày giao và hạn nộp.");
    if (!assignToAll && selectedStudentIds.length === 0)
      return toast.error("Bạn đang giao theo subset — cần chọn ít nhất 1 học viên.");

    try {
      setSaving(true);
      const batch = writeBatch(db);
      const ref = doc(db, "assignments", assignment.id);

      const newAssignedTo = assignToAll ? null : selectedStudentIds;
      const oldAssignedTo = Array.isArray(assignment.assignedTo) ? assignment.assignedTo : [];
      const newlyAddedIds = newAssignedTo ? newAssignedTo.filter((id) => !oldAssignedTo.includes(id)) : [];

      const payload = {
        title: title.trim(),
        note: note.trim(),
        startAt,
        deadline,
        allowLate,
        maxAttempts: Number(maxAttempts) || 1,
        status: isOver(deadline) ? "closed" : "open",
        assignedTo: newAssignedTo,
        updatedAt: serverTimestamp(),
        // NEW:
        published,
      };

      batch.update(ref, payload);

      // Notify logic:
      // - Nếu vừa chuyển từ draft -> published: notify toàn bộ phạm vi (subset hoặc toàn lớp)
      // - Nếu published đã true sẵn: chỉ notify học viên mới được thêm (nếu bật)
      const publishingNow = !assignment.published && published;
      if (sendNotifForNew) {
        let target = [];
        if (publishingNow) {
          target = assignToAll ? students : students.filter((s) => newAssignedTo?.includes(s.id));
        } else if (published && newlyAddedIds.length > 0) {
          target = students.filter((s) => newlyAddedIds.includes(s.id));
        }
        if (target.length > 0) {
          target.forEach((s) => {
            const notifRef = doc(collection(db, "notifications"));
            batch.set(notifRef, {
              toUserId: s.id,
              type: "assignment",
              title: publishingNow ? "Bài tập đã được công khai" : "Cập nhật bài tập",
              message: publishingNow
                ? `Assignment: ${payload.title} đã được công khai (hạn: ${fmt(deadline)})`
                : `Bạn được thêm vào assignment: ${payload.title} (hạn: ${fmt(deadline)})`,
              classId: classInfo.id,
              createdAt: serverTimestamp(),
            });
          });
        }
      }

      await batch.commit();
      await logClassActivity({
        classId: classInfo.id,
        type: "assignment_updated",
        severity: published ? "normal" : "warning",
        targetId: assignment.id,
        targetTitle: payload.title,
        message: `Đã cập nhật bài tập: ${payload.title}${publishingNow ? " và công khai cho học viên" : ""}.`,
        metadata: {
          assignmentId: assignment.id,
          deadline,
          published,
          targetMode: assignToAll ? "all" : "selected",
          targetCount: assignToAll ? students.length : selectedStudentIds.length,
        },
      });
      toast.success("Đã cập nhật assignment!");
      onClose?.();
    } catch (e) {
      console.error(e);
      toast.error("Cập nhật assignment thất bại.");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !assignment) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-50 w-[min(980px,95vw)] max-h-[90vh] overflow-hidden rounded-2xl bg-card border border-border shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-bold">Chỉnh sửa bài tập</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-bg">
            <FiX />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4 p-4 overflow-y-auto" style={{ maxHeight: "75vh" }}>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Tiêu đề</label>
              <input
                className="w-full px-3 py-2 mt-1 rounded-xl border border-border bg-card"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Ghi chú</label>
              <textarea
                rows={4}
                className="w-full px-3 py-2 mt-1 rounded-xl border border-border bg-card"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Ngày giao</label>
                <input
                  type="date"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                  className="w-full px-3 py-2 mt-1 rounded-xl border border-border bg-card"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Hạn nộp</label>
                <input
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  className="w-full px-3 py-2 mt-1 rounded-xl border border-border bg-card"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={allowLate} onChange={(e) => setAllowLate(e.target.checked)} />
                Cho phép nộp trễ
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Số lần nộp tối đa</span>
                <input
                  type="number"
                  min={1}
                  value={maxAttempts}
                  onChange={(e) => setMaxAttempts(e.target.value)}
                  className="w-24 px-3 py-2 rounded-xl border border-border bg-card"
                />
              </div>
            </div>

            {/* NEW: Published toggle */}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
              Công khai (hiển thị cho học viên)
            </label>
          </div>

          {/* Subset */}
          <div className="space-y-3">
            <div className="rounded-xl border border-border">
              <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                <div className="text-sm font-medium">Giao cho</div>
                <label className="text-sm flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={assignToAll}
                    onChange={(e) => {
                      setAssignToAll(e.target.checked);
                      if (e.target.checked) setSelectedStudentIds([]);
                    }}
                  />
                  Toàn lớp
                </label>
              </div>

              {!assignToAll && (
                <div className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      className="flex-1 px-3 py-2 rounded-xl border border-border bg-bg"
                      placeholder="Tìm học viên theo tên/email..."
                      value={studentQuery}
                      onChange={(e) => setStudentQuery(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={handleToggleAllVisible}
                      className="px-3 py-2 rounded-xl border border-border hover:bg-primary/5 text-sm"
                    >
                      {allVisibleChecked ? "Bỏ chọn tất cả đang hiển thị" : "Chọn tất cả đang hiển thị"}
                    </button>
                  </div>

                  <div className="text-xs text-text2">
                    Đã chọn: <b>{selectedStudentIds.length}</b> / {students.length} học viên
                  </div>

                  <div className="max-h-[36vh] overflow-y-auto rounded-lg border border-dashed border-border">
                    {filteredStudents.length === 0 && (
                      <div className="p-3 text-sm text-text2">Không có học viên phù hợp.</div>
                    )}
                    {filteredStudents.map((stu) => {
                      const label = stu.fullName || `${stu.firstName || ""} ${stu.lastName || ""}`.trim();
                      const checked = selectedStudentIds.includes(stu.id);
                      return (
                        <label
                          key={stu.id}
                          className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-0 hover:bg-primary/5 cursor-pointer"
                        >
                          <input type="checkbox" checked={checked} onChange={() => handleToggleOne(stu.id)} />
                          <span className="text-sm">{label}</span>
                          {stu.email && <span className="ml-auto text-xs text-text2">{stu.email}</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={sendNotifForNew} onChange={(e) => setSendNotifForNew(e.target.checked)} />
              Gửi thông báo (áp dụng khi công khai hoặc thêm học viên mới)
            </label>

            <div className="flex justify-end gap-3">
              <Button onClick={onClose} className="rounded-xl bg-bg text-menu">
                Hủy
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="rounded-xl bg-primary text-text-button hover:brightness-95 flex items-center gap-2"
              >
                <FiSend />
                {saving ? "Đang lưu..." : "Lưu thay đổi"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
